/* ModMoss: native Shopify forms with small, optional progressive enhancements. */
(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart`;
  let cartBusy = false;
  const cartLinkState = new WeakMap();

  async function requestJSON(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'That change could not be completed. Please check your selection and try again.');
        const status = response.ok ? Number(data.status) : response.status;
        error.confirmed = status >= 400 && status < 500;
        throw error;
      }
      return data;
    } finally { clearTimeout(timer); }
  }

  function showError(target, error) {
    if (!target) return;
    target.replaceChildren();
    target.textContent = error.confirmed ? error.message : 'We could not confirm that change. Check your cart before trying again to avoid adding it twice. ';
    if (!error.confirmed) {
      const link = document.createElement('a');
      link.href = cartUrl;
      link.textContent = 'View your cart';
      target.append(link);
    }
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function updateVariant(section, updateUrl) {
    const select = section.querySelector('[data-mm-variant]');
    const option = select?.selectedOptions[0];
    if (!option) return;
    const price = section.querySelector('[data-mm-price]');
    const button = section.querySelector('[data-mm-add]');
    const quantity = section.querySelector('input[name="quantity"]');
    const needsReview = section.querySelector('.mm-product-form')?.dataset.needsCartReview === 'true';
    const available = option.dataset.available === 'true' && section.dataset.requiresPlan !== 'true' && !needsReview;
    if (price) price.textContent = option.dataset.price;
    if (button) {
      button.disabled = !available;
      button.textContent = needsReview ? 'Review your cart below' : (available ? 'Add to cart ↗' : (section.dataset.requiresPlan === 'true' ? 'Purchase plan required' : 'Sold out'));
    }
    if (quantity) {
      const min = Number(option.dataset.min) || 1;
      const step = Number(option.dataset.step) || 1;
      const maximum = Number(option.dataset.max) || null;
      quantity.min = String(min);
      quantity.step = String(step);
      if (maximum) quantity.max = String(maximum); else quantity.removeAttribute('max');
      let next = Math.max(min, Number(quantity.value) || min);
      next = min + Math.ceil((next - min) / step) * step;
      if (maximum && next > maximum) next = min + Math.floor((maximum - min) / step) * step;
      quantity.value = String(next);
    }
    if (option.dataset.image) setImage(section, option.dataset.image, option.dataset.imageAlt);
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      history.replaceState({}, '', url);
    }
  }

  function setImage(section, source, alt) {
    const photo = section.querySelector('.mm-product-main-photo');
    if (!photo) return;
    photo.removeAttribute('srcset');
    photo.src = source;
    photo.alt = alt || '';
  }

  function initializeProduct(section) {
    if (section.dataset.mmReady) return;
    section.dataset.mmReady = 'true';
    const select = section.querySelector('[data-mm-variant]');
    select?.addEventListener('change', () => updateVariant(section, true));
    updateVariant(section, false);
    section.querySelectorAll('[data-mm-gallery-image]').forEach(link => link.addEventListener('click', event => {
      event.preventDefault();
      setImage(section, link.dataset.mmGalleryImage, link.dataset.imageAlt);
      section.querySelectorAll('[data-mm-gallery-image]').forEach(other => other.removeAttribute('aria-current'));
      link.setAttribute('aria-current', 'true');
    }));
    const form = section.querySelector('.mm-product-form');
    form?.addEventListener('submit', async event => {
      if (!window.fetch || !window.FormData || !window.AbortController) return;
      event.preventDefault();
      if (form.dataset.busy === 'true' || !form.reportValidity()) return;
      const button = form.querySelector('[data-mm-add]');
      if (button?.disabled) return;
      const body = new FormData(form);
      const status = section.querySelector('[data-mm-product-status]');
      form.dataset.busy = 'true';
      if (button) { button.disabled = true; button.textContent = 'Adding…'; }
      if (status) status.textContent = '';
      try {
        await requestJSON(`${root}cart/add.js`, { method: 'POST', body });
        window.location.assign(cartUrl);
      } catch (error) {
        // Even an inventory error can leave a partial quantity in Shopify's cart.
        // Do not make a second write until the shopper has reviewed that result.
        form.dataset.needsCartReview = 'true';
        showError(status, error);
        if (status && error.confirmed) {
          status.append(document.createTextNode(' Please check your cart before trying again. '));
          const link = document.createElement('a');
          link.href = cartUrl;
          link.textContent = 'View your cart';
          status.append(link);
        }
        updateVariant(section, false);
      } finally { form.dataset.busy = 'false'; }
    });
  }

  function setCartBusy(cart, busy) {
    cartBusy = busy;
    cart.setAttribute('aria-busy', String(busy));
    const blocked = busy || cart.dataset.needsCartReview === 'true';
    cart.querySelectorAll('button, input[type="number"]').forEach(control => { control.disabled = blocked; });
    cart.querySelectorAll('[data-mm-cart-change] a').forEach(link => {
      if (blocked) {
        if (!cartLinkState.has(link)) cartLinkState.set(link, ['href', 'tabindex', 'aria-disabled'].map(name => [name, link.getAttribute(name)]));
        // Remove the mutation URL too, so keyboard and open-in-new-tab cannot bypass the lock.
        link.removeAttribute('href');
        link.setAttribute('tabindex', '-1');
        link.setAttribute('aria-disabled', 'true');
      } else if (cartLinkState.has(link)) {
        cartLinkState.get(link).forEach(([name, value]) => {
          if (value === null) link.removeAttribute(name); else link.setAttribute(name, value);
        });
        cartLinkState.delete(link);
      }
    });
  }

  function initializeCart(cart) {
    if (cart.dataset.mmReady) return;
    cart.dataset.mmReady = 'true';
    const status = cart.querySelector('[data-mm-cart-error]');
    cart.addEventListener('click', event => {
      if ((cartBusy || cart.dataset.needsCartReview === 'true') && event.target.closest('[data-mm-cart-change] a')) event.preventDefault();
    });
    cart.querySelectorAll('[data-mm-cart-change], [data-mm-kit-remove]').forEach(form => form.addEventListener('submit', async event => {
      if (!window.fetch || !window.AbortController) return;
      event.preventDefault();
      if (cartBusy || cart.dataset.needsCartReview === 'true' || !form.reportValidity()) return;
      const values = new FormData(form);
      setCartBusy(cart, true);
      if (status) status.textContent = '';
      try {
        if (form.hasAttribute('data-mm-kit-remove')) {
          const kit = form.dataset.kitId;
          const latest = await requestJSON(`${root}cart.js`, { cache: 'no-store' });
          const members = latest.items.filter(item => item.properties?._mm_kit === kit);
          if (members.length) {
            const updates = Object.fromEntries(members.map(item => [item.key, 0]));
            const result = await requestJSON(`${root}cart/update.js`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates })
            });
            if (result.items.some(item => item.properties?._mm_kit === kit)) {
              throw new Error('The cart changed while removing your kit. Please review it before trying again.');
            }
          }
        } else {
          const quantity = Number(values.get('quantity'));
          if (!Number.isSafeInteger(quantity) || quantity < 0) {
            const error = new Error('Please enter a whole-number quantity of zero or more.');
            error.confirmed = true;
            throw error;
          }
          await requestJSON(`${root}cart/change.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: String(values.get('id')), quantity })
          });
        }
        window.location.reload();
      } catch (error) {
        // A timeout, lost response or server failure may follow a successful write.
        // Keep mutations locked until a fresh cart load shows its actual state.
        if (!error.confirmed) cart.dataset.needsCartReview = 'true';
        showError(status, error);
        setCartBusy(cart, false);
      }
    }));
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-mm-product]').forEach(initializeProduct);
    scope.querySelectorAll('[data-mm-cart]').forEach(initializeCart);
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
